import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { Room } from "../entities/room.entity";
import { FilterRoomsInputs, RoomWithDetails, UserSummary } from "../types";
import { roomRepository } from "../repositories/room.repository";
import { buildRoomsFilter } from "../utils/buildRoomsQuery";
import { userRepository } from "src/features/users/repositories/user.repository";
import { onSnapshot } from "firebase/firestore";

const roomsService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    // List rooms with pagination and filtering
    getRooms: builder.query<Room[], { where: FilterRoomsInputs; offset: string }>({
      queryFn: ({ where, offset }) =>
        executeQuery(async () => {
          const safeWhere: FilterRoomsInputs = where ?? ({} as FilterRoomsInputs);
          const filter = await buildRoomsFilter(safeWhere);
          return roomRepository.find({
            where: filter,
            offset,
            orderBy: ["startTime", "desc"],
          });
        }),
    }),

    // Count rooms for pagination
    countRooms: builder.query<number, { where: FilterRoomsInputs }>({
      queryFn: ({ where }) =>
        executeQuery(async () => {
          const safeWhere: FilterRoomsInputs = where ?? ({} as FilterRoomsInputs);
          const filter = await buildRoomsFilter(safeWhere);
          return filter.id ? 1 : roomRepository.count(filter);
        }),
    }),

    // Get single room with real-time updates
    getRoomById: builder.query<RoomWithDetails, string>({
      queryFn: (id) =>
        executeQuery(async () => {
          const room = await roomRepository.findById(id);
          if (!room) return null;

          const [participants, history, messages, hostUser] = await Promise.all([
            roomRepository.getParticipants(id),
            roomRepository.getHistory(id),
            roomRepository.getMessages(id),
            userRepository.findById(room.userId),
          ]);

          const host: UserSummary | null = hostUser
            ? {
                id: hostUser.id,
                name: hostUser.name,
                email: hostUser.email,
                pictureURL: hostUser.pictureURL,
                phoneNumber: hostUser.phoneNumber,
              }
            : null;

          return { room, participants, history, messages, host };
        }),

      // Real-time updates using Firestore onSnapshot
      async onCacheEntryAdded(
        roomId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        // Wait for initial data to be loaded
        await cacheDataLoaded;

        // Set up real-time listener for the room document
        const roomDocRef = roomRepository.getRoomDocRef(roomId);

        const unsubscribeRoom = onSnapshot(
          roomDocRef,
          { includeMetadataChanges: true },
          async (snapshot) => {
            if (!snapshot.exists()) return;

            const roomData = { ...snapshot.data(), id: snapshot.id } as Room;

            // Fetch related data
            const [participants, history, messages, hostUser] = await Promise.all([
              roomRepository.getParticipants(roomId),
              roomRepository.getHistory(roomId),
              roomRepository.getMessages(roomId),
              userRepository.findById(roomData.userId),
            ]);

            const host: UserSummary | null = hostUser
              ? {
                  id: hostUser.id,
                  name: hostUser.name,
                  email: hostUser.email,
                  pictureURL: hostUser.pictureURL,
                  phoneNumber: hostUser.phoneNumber,
                }
              : null;

            // Update the cache with new data
            updateCachedData((draft) => {
              draft.room = roomData;
              draft.participants = participants;
              draft.history = history;
              draft.messages = messages;
              draft.host = host;
            });
          },
          (error) => {
            console.error("Error listening to room updates:", error);
          }
        );

        // Set up real-time listener for participants sub-collection
        const participantsQuery = roomRepository.getParticipantsQuery(roomId);

        const unsubscribeParticipants = onSnapshot(
          participantsQuery,
          { includeMetadataChanges: true },
          async (snapshot) => {
            const participants = snapshot.docs.map(
              (doc) => ({ ...doc.data(), id: doc.id } as any)
            );

            updateCachedData((draft) => {
              draft.participants = participants;
            });
          },
          (error) => {
            console.error("Error listening to participants updates:", error);
          }
        );

        // Clean up listeners when cache entry is removed
        await cacheEntryRemoved;
        unsubscribeRoom();
        unsubscribeParticipants();
      },
    }),
  }),
});

export const {
  useGetRoomsQuery,
  useCountRoomsQuery,
  useGetRoomByIdQuery,
} = roomsService;
